import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Search, Download, Eye, Edit2, Trash2, X,
  AlertCircle, Building2, Globe, Phone, Mail,
  RefreshCw, ChevronLeft, ChevronRight, CheckCircle,
  Palette, Shield, Bell, Settings
} from 'lucide-react';
import { companySettingsController } from '../../controllers/companySettingsController';

// ─── helpers ──────────────────────────────────────────────
const EMPTY_FORM = {
  companyName: '', legalName: '', registrationNumber: '', taxId: '',
  industry: '', companySize: '', establishedYear: '',
  description: '', email: '', phone: '', fax: '', website: '',
  address: { street: '', city: '', state: '', country: '', zipCode: '' },
  primaryColor: '#3b82f6', secondaryColor: '#8b5cf6', accentColor: '#10b981',
  timezone: 'Asia/Kolkata', currency: 'INR', language: 'en',
};

const INDUSTRIES = [
  'Technology', 'Finance', 'Healthcare', 'Education', 'Retail',
  'Manufacturing', 'Real Estate', 'Hospitality', 'Logistics', 'Other',
];
const SIZES = ['1-10', '11-50', '51-200', '201-500', '500+'];

const statusBadge = (active) => ({
  backgroundColor: active ? '#d1fae5' : '#fee2e2',
  color: active ? '#065f46' : '#991b1b',
  padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500',
});

// ─── main component ───────────────────────────────────────
const CompanyManagementPage = () => {
  const [companies,   setCompanies]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [pagination,  setPagination]  = useState({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [search,      setSearch]      = useState('');
  const [sortBy,      setSortBy]      = useState('createdAt');
  const [order,       setOrder]       = useState('desc');

  const [modal,       setModal]       = useState(null); // null | 'create' | 'edit' | 'view'
  const [selected,    setSelected]    = useState(null);
  const [formData,    setFormData]    = useState(EMPTY_FORM);
  const [formErrors,  setFormErrors]  = useState([]);
  const [submitting,  setSubmitting]  = useState(false);
  const [activeTab,   setActiveTab]   = useState('general');

  // ── fetch ────────────────────────────────────────────────
  const fetchCompanies = useCallback(async (params = {}) => {
    setLoading(true);
    const result = await companySettingsController.getAllCompanies({
      page: pagination.page, limit: pagination.limit,
      search, sortBy, order, ...params,
    });
    if (result.success) {
      setCompanies(result.data);
      setPagination(result.pagination);
    }
    setLoading(false);
  }, [pagination.page, pagination.limit, search, sortBy, order]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  // ── debounce search ──────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => fetchCompanies({ page: 1 }), 400);
    return () => clearTimeout(t);
  }, [search]);

  // ── form helpers ─────────────────────────────────────────
  const openCreate = () => {
    setFormData(EMPTY_FORM); setFormErrors([]); setActiveTab('general'); setModal('create');
  };
  const openEdit = (c) => {
    setSelected(c);
    setFormData({
      companyName: c.companyName || '', legalName: c.legalName || '',
      registrationNumber: c.registrationNumber || '', taxId: c.taxId || '',
      industry: c.industry || '', companySize: c.companySize || '',
      establishedYear: c.establishedYear || '', description: c.description || '',
      email: c.email || '', phone: c.phone || '', fax: c.fax || '',
      website: c.website || '',
      address: { street: c.address?.street || '', city: c.address?.city || '',
        state: c.address?.state || '', country: c.address?.country || '',
        zipCode: c.address?.zipCode || '' },
      primaryColor: c.primaryColor || '#3b82f6',
      secondaryColor: c.secondaryColor || '#8b5cf6',
      accentColor: c.accentColor || '#10b981',
      timezone: c.timezone || 'Asia/Kolkata',
      currency: c.currency || 'INR', language: c.language || 'en',
    });
    setFormErrors([]); setActiveTab('general'); setModal('edit');
  };
  const openView = (c) => { setSelected(c); setModal('view'); };
  const closeModal = () => { setModal(null); setSelected(null); setFormErrors([]); };

  const setField = (name, value) => {
    setFormErrors([]);
    if (name.startsWith('address.')) {
      const key = name.split('.')[1];
      setFormData(p => ({ ...p, address: { ...p.address, [key]: value } }));
    } else {
      setFormData(p => ({ ...p, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setFormErrors([]);
    const errs = [];
    if (!formData.companyName) errs.push('Company name is required');
    if (!formData.email)       errs.push('Email is required');
    if (errs.length) { setFormErrors(errs); setSubmitting(false); return; }

    const result = modal === 'create'
      ? await companySettingsController.initSettings(formData)
      : await companySettingsController.updateSettings(formData);

    if (result.success) {
      closeModal(); fetchCompanies();
    } else {
      setFormErrors([result.error || result.message || 'Something went wrong']);
    }
    setSubmitting(false);
  };

  // ── styles ───────────────────────────────────────────────
  const S = {
    page: { padding: 0, fontFamily: "'DM Sans', sans-serif" },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    title: { fontSize: '26px', fontWeight: '700', color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
    sub: { fontSize: '14px', color: '#64748b', marginTop: '4px' },
    btnPrimary: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: '#0f172a', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'opacity .15s' },
    btnSecondary: { display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 16px', backgroundColor: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', cursor: 'pointer' },
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '14px', marginBottom: '24px' },
    stat: { background: '#fff', borderRadius: '12px', border: '1px solid #f1f5f9', padding: '18px 20px' },
    statLabel: { fontSize: '13px', color: '#64748b', marginBottom: '6px' },
    statVal: { fontSize: '26px', fontWeight: '700', color: '#0f172a' },
    toolbar: { display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' },
    searchWrap: { flex: 1, minWidth: '220px', position: 'relative' },
    searchInput: { width: '100%', padding: '10px 12px 10px 38px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '14px', outline: 'none', background: '#fff', boxSizing: 'border-box' },
    searchIcon: { position: 'absolute', left: '11px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' },
    sel: { padding: '9px 14px', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '13px', outline: 'none', background: '#fff', cursor: 'pointer', color: '#374151' },
    card: { background: '#fff', borderRadius: '14px', border: '1px solid #f1f5f9', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { background: '#f8fafc', padding: '14px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #f1f5f9', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' },
    td: { padding: '14px 16px', borderBottom: '1px solid #f8fafc', fontSize: '13px', color: '#1e293b', verticalAlign: 'middle' },
    tdMuted: { fontSize: '12px', color: '#94a3b8', marginTop: '2px' },
    actBtn: { padding: '6px', background: 'transparent', border: 'none', borderRadius: '7px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' },
    pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap', gap: '8px' },
    pagerInfo: { fontSize: '13px', color: '#64748b' },
    // modal
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '16px' },
    modalBox: { background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '720px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,.18)' },
    mHead: { padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, background: '#fff', zIndex: 1 },
    mTitle: { fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 },
    mBody: { padding: '24px' },
    mFoot: { padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '10px', position: 'sticky', bottom: 0, background: '#fff' },
    tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #f1f5f9', paddingBottom: '0' },
    tab: (active) => ({ padding: '8px 16px', fontSize: '13px', fontWeight: '500', border: 'none', background: 'transparent', cursor: 'pointer', color: active ? '#0f172a' : '#64748b', borderBottom: active ? '2px solid #0f172a' : '2px solid transparent', marginBottom: '-1px', display: 'flex', alignItems: 'center', gap: '6px' }),
    fg: { marginBottom: '16px' },
    label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' },
    inp: { width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: '9px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', background: '#fff', color: '#1e293b' },
    row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
    errBox: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '9px', padding: '12px 16px', marginBottom: '18px' },
    errItem: { color: '#dc2626', fontSize: '13px', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' },
    colorWrap: { display: 'flex', alignItems: 'center', gap: '8px' },
    colorSwatch: (val) => ({ width: '32px', height: '32px', borderRadius: '8px', border: '1px solid #e5e7eb', background: val, flexShrink: 0 }),
  };

  // ── tab sections ─────────────────────────────────────────
  const TabGeneral = () => (
    <>
      <div style={S.row2}>
        <div style={S.fg}><label style={S.label}>Company name *</label><input style={S.inp} value={formData.companyName} onChange={e => setField('companyName', e.target.value)} placeholder="Acme Corp" /></div>
        <div style={S.fg}><label style={S.label}>Legal name</label><input style={S.inp} value={formData.legalName} onChange={e => setField('legalName', e.target.value)} /></div>
        <div style={S.fg}><label style={S.label}>Registration number</label><input style={S.inp} value={formData.registrationNumber} onChange={e => setField('registrationNumber', e.target.value)} /></div>
        <div style={S.fg}><label style={S.label}>Tax ID / GST</label><input style={S.inp} value={formData.taxId} onChange={e => setField('taxId', e.target.value)} /></div>
        <div style={S.fg}><label style={S.label}>Industry</label>
          <select style={S.inp} value={formData.industry} onChange={e => setField('industry', e.target.value)}>
            <option value="">Select industry</option>
            {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
          </select>
        </div>
        <div style={S.fg}><label style={S.label}>Company size</label>
          <select style={S.inp} value={formData.companySize} onChange={e => setField('companySize', e.target.value)}>
            <option value="">Select size</option>
            {SIZES.map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div style={S.fg}><label style={S.label}>Established year</label><input style={S.inp} type="number" value={formData.establishedYear} onChange={e => setField('establishedYear', e.target.value)} placeholder="2020" /></div>
      </div>
      <div style={S.fg}><label style={S.label}>Description</label><textarea style={{ ...S.inp, minHeight: '80px', resize: 'vertical' }} value={formData.description} onChange={e => setField('description', e.target.value)} /></div>
    </>
  );

  const TabContact = () => (
    <>
      <div style={S.row2}>
        <div style={S.fg}><label style={S.label}>Email *</label><input style={S.inp} type="email" value={formData.email} onChange={e => setField('email', e.target.value)} /></div>
        <div style={S.fg}><label style={S.label}>Phone</label><input style={S.inp} value={formData.phone} onChange={e => setField('phone', e.target.value)} /></div>
        <div style={S.fg}><label style={S.label}>Website</label><input style={S.inp} value={formData.website} onChange={e => setField('website', e.target.value)} placeholder="https://" /></div>
        <div style={S.fg}><label style={S.label}>Fax</label><input style={S.inp} value={formData.fax} onChange={e => setField('fax', e.target.value)} /></div>
      </div>
      <p style={{ fontSize: '13px', fontWeight: '600', color: '#374151', margin: '8px 0 14px' }}>Address</p>
      <div style={S.fg}><label style={S.label}>Street</label><input style={S.inp} value={formData.address.street} onChange={e => setField('address.street', e.target.value)} /></div>
      <div style={S.row2}>
        <div style={S.fg}><label style={S.label}>City</label><input style={S.inp} value={formData.address.city} onChange={e => setField('address.city', e.target.value)} /></div>
        <div style={S.fg}><label style={S.label}>State</label><input style={S.inp} value={formData.address.state} onChange={e => setField('address.state', e.target.value)} /></div>
        <div style={S.fg}><label style={S.label}>Country</label><input style={S.inp} value={formData.address.country} onChange={e => setField('address.country', e.target.value)} /></div>
        <div style={S.fg}><label style={S.label}>Zip / PIN</label><input style={S.inp} value={formData.address.zipCode} onChange={e => setField('address.zipCode', e.target.value)} /></div>
      </div>
    </>
  );

  const TabBranding = () => (
    <>
      <div style={S.row2}>
        {[['primaryColor','Primary color'],['secondaryColor','Secondary color'],['accentColor','Accent color']].map(([key, lbl]) => (
          <div key={key} style={S.fg}>
            <label style={S.label}>{lbl}</label>
            <div style={S.colorWrap}>
              <div style={S.colorSwatch(formData[key])} />
              <input style={{ ...S.inp, flex: 1 }} type="color" value={formData[key]} onChange={e => setField(key, e.target.value)} />
              <input style={{ ...S.inp, flex: 2 }} value={formData[key]} onChange={e => setField(key, e.target.value)} placeholder="#3b82f6" />
            </div>
          </div>
        ))}
      </div>
    </>
  );

  const TabPrefs = () => (
    <div style={S.row2}>
      <div style={S.fg}><label style={S.label}>Timezone</label>
        <select style={S.inp} value={formData.timezone} onChange={e => setField('timezone', e.target.value)}>
          {['Asia/Kolkata','UTC','America/New_York','Europe/London','Asia/Dubai','Asia/Singapore'].map(tz => <option key={tz}>{tz}</option>)}
        </select>
      </div>
      <div style={S.fg}><label style={S.label}>Currency</label>
        <select style={S.inp} value={formData.currency} onChange={e => setField('currency', e.target.value)}>
          {['INR','USD','EUR','GBP','AED','SGD'].map(c => <option key={c}>{c}</option>)}
        </select>
      </div>
      <div style={S.fg}><label style={S.label}>Language</label>
        <select style={S.inp} value={formData.language} onChange={e => setField('language', e.target.value)}>
          {[['en','English'],['hi','Hindi'],['ar','Arabic'],['fr','French']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    </div>
  );

  const TABS = [
    { id: 'general',  label: 'General',   icon: Building2 },
    { id: 'contact',  label: 'Contact',   icon: Phone },
    { id: 'branding', label: 'Branding',  icon: Palette },
    { id: 'prefs',    label: 'Preferences', icon: Settings },
  ];

  // ── view modal helper ────────────────────────────────────
  const ViewRow = ({ label, value }) => value ? (
    <div style={{ display: 'flex', padding: '10px 0', borderBottom: '1px solid #f8fafc' }}>
      <span style={{ fontSize: '13px', color: '#64748b', minWidth: '160px', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: '500', wordBreak: 'break-all' }}>{value}</span>
    </div>
  ) : null;

  const start = (pagination.page - 1) * pagination.limit + 1;
  const end   = Math.min(pagination.page * pagination.limit, pagination.total);

  // ── render ───────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Company Management</h1>
          <p style={S.sub}>Manage company configurations and white-label settings</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={S.btnSecondary} onClick={() => fetchCompanies()}>
            <RefreshCw size={15} /> Refresh
          </button>
          <button style={S.btnSecondary}>
            <Download size={15} /> Export
          </button>
          <button style={S.btnPrimary} onClick={openCreate}>
            <Plus size={16} /> Add Company
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={S.statsRow}>
        <div style={S.stat}><div style={S.statLabel}>Total companies</div><div style={S.statVal}>{pagination.total}</div></div>
        <div style={S.stat}><div style={S.statLabel}>Active</div><div style={{ ...S.statVal, color: '#10b981' }}>{companies.filter(c => c.isActive !== false).length}</div></div>
        <div style={S.stat}><div style={S.statLabel}>Inactive</div><div style={{ ...S.statVal, color: '#f59e0b' }}>{companies.filter(c => c.isActive === false).length}</div></div>
        <div style={S.stat}><div style={S.statLabel}>Current page</div><div style={S.statVal}>{pagination.page} / {pagination.totalPages}</div></div>
      </div>

      {/* Toolbar */}
      <div style={S.toolbar}>
        <div style={S.searchWrap}>
          <Search size={16} style={S.searchIcon} />
          <input style={S.searchInput} placeholder="Search by name, email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select style={S.sel} value={sortBy} onChange={e => { setSortBy(e.target.value); fetchCompanies({ sortBy: e.target.value }); }}>
          <option value="createdAt">Sort: Date</option>
          <option value="companyName">Sort: Name</option>
          <option value="email">Sort: Email</option>
        </select>
        <select style={S.sel} value={order} onChange={e => { setOrder(e.target.value); fetchCompanies({ order: e.target.value }); }}>
          <option value="desc">Descending</option>
          <option value="asc">Ascending</option>
        </select>
        <select style={S.sel} value={pagination.limit} onChange={e => setPagination(p => ({ ...p, limit: Number(e.target.value), page: 1 }))}>
          {[10,20,50].map(n => <option key={n} value={n}>{n} / page</option>)}
        </select>
      </div>

      {/* Table */}
      <div style={S.card}>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
            <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
            <p style={{ marginTop: '12px', fontSize: '14px' }}>Loading companies...</p>
          </div>
        ) : companies.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8' }}>
            <Building2 size={40} style={{ marginBottom: '12px', opacity: .4 }} />
            <p style={{ fontSize: '15px', fontWeight: '500' }}>No companies found</p>
            <p style={{ fontSize: '13px', marginTop: '4px' }}>Create your first company to get started</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>Company</th>
                    <th style={S.th}>Contact</th>
                    <th style={S.th}>Industry</th>
                    <th style={S.th}>Currency</th>
                    <th style={S.th}>Status</th>
                    <th style={S.th}>Branding</th>
                    <th style={S.th}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c._id} style={{ cursor: 'pointer' }}>
                      <td style={S.td} onClick={() => openView(c)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {c.logo ? (
                            <img src={c.logo} alt="" style={{ width: '32px', height: '32px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #f1f5f9' }} />
                          ) : (
                            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: c.primaryColor || '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '13px', flexShrink: 0 }}>
                              {(c.companyName || 'C')[0].toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '13px' }}>{c.companyName}</div>
                            <div style={S.tdMuted}>{c.legalName || c.registrationNumber || ''}</div>
                          </div>
                        </div>
                      </td>
                      <td style={S.td}>
                        <div style={{ fontSize: '13px' }}>{c.email}</div>
                        <div style={S.tdMuted}>{c.phone}</div>
                      </td>
                      <td style={S.td}>{c.industry || '—'}</td>
                      <td style={S.td}>{c.currency || 'INR'}</td>
                      <td style={S.td}><span style={statusBadge(c.isActive !== false)}>{c.isActive !== false ? 'Active' : 'Inactive'}</span></td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', gap: '4px' }}>
                          {[c.primaryColor, c.secondaryColor, c.accentColor].filter(Boolean).map((col, i) => (
                            <div key={i} style={{ width: '16px', height: '16px', borderRadius: '4px', background: col, border: '1px solid #e5e7eb' }} title={col} />
                          ))}
                        </div>
                      </td>
                      <td style={S.td}>
                        <div style={{ display: 'flex', gap: '2px' }}>
                          <button style={S.actBtn} onClick={() => openView(c)} title="View"><Eye size={16} color="#64748b" /></button>
                          <button style={S.actBtn} onClick={() => openEdit(c)} title="Edit"><Edit2 size={16} color="#3b82f6" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div style={S.pager}>
              <span style={S.pagerInfo}>Showing {start}–{end} of {pagination.total}</span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <button style={S.btnSecondary} disabled={pagination.page <= 1}
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
                  <ChevronLeft size={16} />
                </button>
                <span style={{ fontSize: '13px', color: '#374151', padding: '0 4px' }}>
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button style={S.btnSecondary} disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Create / Edit Modal ── */}
      {(modal === 'create' || modal === 'edit') && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.mHead}>
              <h2 style={S.mTitle}>{modal === 'create' ? 'Add New Company' : `Edit — ${selected?.companyName}`}</h2>
              <button style={S.actBtn} onClick={closeModal}><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={S.mBody}>
                {/* Tabs */}
                <div style={S.tabs}>
                  {TABS.map(({ id, label, icon: Icon }) => (
                    <button key={id} type="button" style={S.tab(activeTab === id)} onClick={() => setActiveTab(id)}>
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>

                {/* Errors */}
                {formErrors.length > 0 && (
                  <div style={S.errBox}>
                    {formErrors.map((e, i) => (
                      <div key={i} style={S.errItem}><AlertCircle size={13} />{e}</div>
                    ))}
                  </div>
                )}

                {activeTab === 'general'  && <TabGeneral />}
                {activeTab === 'contact'  && <TabContact />}
                {activeTab === 'branding' && <TabBranding />}
                {activeTab === 'prefs'    && <TabPrefs />}
              </div>

              <div style={S.mFoot}>
                <button type="button" style={S.btnSecondary} onClick={closeModal} disabled={submitting}>Cancel</button>
                <button type="submit" style={S.btnPrimary} disabled={submitting}>
                  {submitting ? 'Saving...' : (modal === 'create' ? 'Create Company' : 'Save Changes')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modal === 'view' && selected && (
        <div style={S.overlay} onClick={closeModal}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.mHead}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: selected.primaryColor || '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: '700', fontSize: '16px' }}>
                  {(selected.companyName || 'C')[0].toUpperCase()}
                </div>
                <div>
                  <h2 style={S.mTitle}>{selected.companyName}</h2>
                  <span style={statusBadge(selected.isActive !== false)}>{selected.isActive !== false ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
              <button style={S.actBtn} onClick={closeModal}><X size={20} /></button>
            </div>
            <div style={S.mBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
                <div>
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '10px' }}>Company Info</p>
                  <ViewRow label="Legal Name"    value={selected.legalName} />
                  <ViewRow label="Reg. Number"   value={selected.registrationNumber} />
                  <ViewRow label="Tax ID / GST"  value={selected.taxId} />
                  <ViewRow label="Industry"      value={selected.industry} />
                  <ViewRow label="Size"          value={selected.companySize} />
                  <ViewRow label="Est. Year"     value={selected.establishedYear} />
                </div>
                <div>
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '10px' }}>Contact</p>
                  <ViewRow label="Email"    value={selected.email} />
                  <ViewRow label="Phone"    value={selected.phone} />
                  <ViewRow label="Website"  value={selected.website} />
                  <ViewRow label="Timezone" value={selected.timezone} />
                  <ViewRow label="Currency" value={selected.currency} />
                </div>
              </div>
              {selected.description && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '8px' }}>Description</p>
                  <p style={{ fontSize: '13px', color: '#374151', lineHeight: '1.6', background: '#f8fafc', padding: '12px', borderRadius: '8px' }}>{selected.description}</p>
                </div>
              )}
              {/* Color swatches */}
              <div>
                <p style={{ fontSize: '11px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '10px' }}>Branding colors</p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {[['Primary', selected.primaryColor],['Secondary', selected.secondaryColor],['Accent', selected.accentColor]].map(([lbl, col]) => col && (
                    <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: col, border: '1px solid #e5e7eb' }} />
                      <div><div style={{ fontSize: '11px', color: '#64748b' }}>{lbl}</div><div style={{ fontSize: '12px', fontWeight: '500' }}>{col}</div></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div style={S.mFoot}>
              <button style={S.btnSecondary} onClick={closeModal}>Close</button>
              <button style={S.btnPrimary} onClick={() => { closeModal(); openEdit(selected); }}>
                <Edit2 size={15} /> Edit Company
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default CompanyManagementPage;